import { z } from "zod";
import { emailDesignSchema } from "./email-design.js";

// ── template ────────────────────────────────────────────────────────
/**
 * Un template può arrivare come design a blocchi (`designJson`, editor
 * drag & drop — l'API compila l'HTML), come HTML libero (`bodyHtml`,
 * modalità avanzata / template legacy) oppure come testo semplice
 * (`bodyText`, spedito come testo puro senza layout né tracking). Almeno
 * uno dei tre deve essere presente: la verifica è nelle route (lo schema
 * resta un ZodObject per poter usare `.partial()` nelle PATCH).
 */
export const templateInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  subject: z.string().trim().min(1).max(300),
  bodyHtml: z.string().trim().min(1).max(100000).optional(),
  bodyText: z.string().trim().min(1).max(50000).nullable().optional(),
  designJson: emailDesignSchema.nullable().optional(),
});
export type TemplateInput = z.infer<typeof templateInputSchema>;

/**
 * Invio di prova dall'editor template: il contenuto corrente (anche non
 * ancora salvato) viene compilato server-side con dati d'esempio e spedito
 * all'indirizzo indicato. Come per l'input template, serve almeno uno tra
 * `designJson` e `bodyHtml` (verifica nelle route).
 */
export const templateTestSendSchema = z.object({
  to: z.string().trim().toLowerCase().email().max(254),
  subject: z.string().trim().min(1).max(300),
  bodyHtml: z.string().trim().min(1).max(100000).optional(),
  bodyText: z.string().trim().min(1).max(50000).nullable().optional(),
  designJson: emailDesignSchema.nullable().optional(),
});
export type TemplateTestSendInput = z.infer<typeof templateTestSendSchema>;

/** Variabili disponibili nei template: {{firstName}} {{lastName}} {{companyName}} {{tenantName}} {{logoUrl}} */
export const TEMPLATE_VARIABLES = [
  "firstName",
  "lastName",
  "companyName",
  "tenantName",
  "logoUrl",
] as const;

export interface TemplateContext {
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  tenantName?: string | null;
  /** URL pubblico del logo caricato nelle Impostazioni azienda. */
  logoUrl?: string | null;
}

export function renderTemplateString(input: string, ctx: TemplateContext): string {
  return input.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, key: string) => {
    const value = (ctx as Record<string, string | null | undefined>)[key];
    return value ?? "";
  });
}

/**
 * Trascrizione HTML di un template in testo semplice: solo escaping e a capo,
 * nessun layout. È la parte HTML "neutra" del multipart (l'invio resta testo
 * puro) e l'anteprima nell'editor.
 */
export function renderPlainTextEmail(text: string): string {
  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;font-size:14px;line-height:1.6;color:#111;white-space:pre-wrap;word-wrap:break-word">${escaped}</div>`;
}

// ── segmenti ────────────────────────────────────────────────────────
export const segmentFiltersSchema = z.object({
  /** Sempre applicato negli invii marketing; qui è un filtro esplicito extra. */
  marketingConsent: z.boolean().optional(),
  city: z.string().trim().max(120).optional().nullable(),
  companyId: z.string().uuid().optional().nullable(),
  hasOpenDeal: z.boolean().optional(),
  createdAfter: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
});
export type SegmentFilters = z.infer<typeof segmentFiltersSchema>;

export const segmentInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  filters: segmentFiltersSchema.default({}),
});

// ── campagne ────────────────────────────────────────────────────────
export const CAMPAIGN_STATUSES = ["draft", "scheduled", "sending", "sent", "canceled"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

/** Destinatari: un segmento dinamico OPPURE una lista statica (esattamente uno). */
export const campaignInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    templateId: z.string().uuid(),
    segmentId: z.string().uuid().optional().nullable(),
    listId: z.string().uuid().optional().nullable(),
  })
  .superRefine((v, ctx) => {
    if (Boolean(v.segmentId) === Boolean(v.listId)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "segment_or_list_required" });
    }
  });
export type CampaignInput = z.infer<typeof campaignInputSchema>;

export const campaignScheduleSchema = z.object({
  at: z.string().datetime({ offset: true }),
});

// ── automazioni ─────────────────────────────────────────────────────
export const AUTOMATION_TRIGGERS = [
  "contact_created",
  "deal_stage_changed",
  "quote_accepted",
] as const;
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number];

export const AUTOMATION_ACTIONS = ["send_email", "create_activity", "wait", "condition"] as const;
export type AutomationAction = (typeof AUTOMATION_ACTIONS)[number];

/**
 * Destinatario dello step `send_email`:
 * - `contact` (default): il lead/contatto del run — email marketing a tutti
 *   gli effetti (consenso richiesto, footer di disiscrizione, tracking);
 * - `custom`: un indirizzo prefissato (`recipientEmail`) — notifica interna,
 *   senza verifica consenso, disiscrizione né tracking (consuma comunque la
 *   quota email mensile del piano).
 */
export const AUTOMATION_EMAIL_RECIPIENTS = ["contact", "custom"] as const;
export type AutomationEmailRecipient = (typeof AUTOMATION_EMAIL_RECIPIENTS)[number];

/** Verifiche disponibili per i nodi `condition` (rami sì/no). */
export const AUTOMATION_CONDITIONS = [
  "contact_has_consent",
  "contact_city_is",
  "contact_has_company",
  "contact_has_open_deal",
  "deal_value_at_least",
  "deal_status_is",
] as const;
export type AutomationCondition = (typeof AUTOMATION_CONDITIONS)[number];

export const AUTOMATION_BRANCHES = ["yes", "no"] as const;
export type AutomationBranch = (typeof AUTOMATION_BRANCHES)[number];

/**
 * Step di automazione. Due formati accettati:
 * - lineare (legacy): senza `id`, l'ordine dell'array è la catena;
 * - grafo: ogni step ha `id`, punta al padre con `parentId` e, se il padre
 *   è un nodo `condition`, indica il ramo (`branch`: yes/no).
 */
export const automationStepSchema = z
  .object({
    id: z.string().uuid().optional(),
    parentId: z.string().uuid().nullable().optional(),
    branch: z.enum(AUTOMATION_BRANCHES).nullable().optional(),
    actionType: z.enum(AUTOMATION_ACTIONS),
    config: z
      .object({
        templateId: z.string().uuid().optional(),      // send_email
        recipient: z.enum(AUTOMATION_EMAIL_RECIPIENTS).optional(), // send_email (default: contact)
        recipientEmail: z.string().trim().toLowerCase().email().max(320).optional(), // send_email + recipient=custom
        title: z.string().trim().max(300).optional(),  // create_activity
        dueInDays: z.coerce.number().int().min(0).max(365).optional(),
        days: z.coerce.number().min(0).max(365).optional(), // wait
        kind: z.enum(AUTOMATION_CONDITIONS).optional(),     // condition
        value: z.union([z.string().trim().max(200), z.number()]).optional(),
      })
      .default({}),
  })
  .superRefine((s, ctx) => {
    if (s.actionType === "send_email" && s.config.recipient === "custom" && !s.config.recipientEmail) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "recipient_email_required",
        path: ["config", "recipientEmail"],
      });
    }
  });

export const automationInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  isActive: z.boolean().default(true),
  triggerType: z.enum(AUTOMATION_TRIGGERS),
  triggerConfig: z
    .object({
      stageId: z.string().uuid().optional(), // deal_stage_changed
      formId: z.string().uuid().optional(), // contact_created: solo i contatti arrivati da questo web form
    })
    .default({}),
  steps: z
    .array(automationStepSchema)
    .min(1)
    .max(50)
    .superRefine((steps, ctx) => {
      const withId = steps.filter((s) => s.id);
      if (withId.length === 0) return; // formato lineare
      if (withId.length !== steps.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "graph_steps_require_id" });
        return;
      }
      const ids = new Set(steps.map((s) => s.id!));
      if (ids.size !== steps.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "duplicate_step_id" });
        return;
      }
      const roots = steps.filter((s) => !s.parentId);
      if (roots.length !== 1) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "exactly_one_root_required" });
        return;
      }
      for (const s of steps) {
        if (s.parentId && !ids.has(s.parentId)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: "unknown_parent_id" });
          return;
        }
      }
      // tutti i nodi devono essere raggiungibili dalla radice (niente cicli)
      const children = new Map<string, string[]>();
      for (const s of steps) {
        if (!s.parentId) continue;
        children.set(s.parentId, [...(children.get(s.parentId) ?? []), s.id!]);
      }
      const seen = new Set<string>();
      const stack = [roots[0]!.id!];
      while (stack.length > 0) {
        const cur = stack.pop()!;
        if (seen.has(cur)) continue;
        seen.add(cur);
        stack.push(...(children.get(cur) ?? []));
      }
      if (seen.size !== steps.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "unreachable_steps" });
      }
    }),
});
export type AutomationInput = z.infer<typeof automationInputSchema>;

// ── web form ────────────────────────────────────────────────────────
/** Tipi di campo disponibili nell'editor dei form. */
export const WEB_FORM_FIELD_TYPES = [
  "text",
  "email",
  "phone",
  "textarea",
  "select",
  "checkbox",
] as const;
export type WebFormFieldType = (typeof WEB_FORM_FIELD_TYPES)[number];

/** Chiavi mappate direttamente sui campi del contatto CRM. */
export const WEB_FORM_STANDARD_KEYS = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "company",
  "message",
] as const;
export type WebFormStandardKey = (typeof WEB_FORM_STANDARD_KEYS)[number];

export const webFormFieldSchema = z.object({
  /** Id stabile per editor/drag & drop. */
  id: z.string().min(1).max(40),
  /** Attributo `name` dell'input nell'HTML incorporato. */
  key: z
    .string()
    .trim()
    .regex(/^[a-zA-Z][a-zA-Z0-9_]{0,59}$/),
  type: z.enum(WEB_FORM_FIELD_TYPES),
  label: z.string().trim().min(1).max(120),
  placeholder: z.string().trim().max(200).default(""),
  required: z.boolean().default(false),
  /** Solo per i campi select. */
  options: z.array(z.string().trim().min(1).max(120)).max(50).default([]),
});
export type WebFormField = z.infer<typeof webFormFieldSchema>;

export const webFormFieldsSchema = z
  .array(webFormFieldSchema)
  .min(1)
  .max(30)
  .superRefine((fields, ctx) => {
    const keys = fields.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "duplicate_field_key" });
    }
    const email = fields.find((f) => f.key === "email");
    if (!email || email.type !== "email" || !email.required) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "email_field_required" });
    }
    for (const f of fields) {
      if (f.type === "select" && f.options.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "select_needs_options" });
      }
    }
  });

/** Set di campi predefinito (identico al form storico). */
export function defaultWebFormFields(): WebFormField[] {
  return [
    { id: "firstName", key: "firstName", type: "text", label: "Nome", placeholder: "Nome", required: true, options: [] },
    { id: "lastName", key: "lastName", type: "text", label: "Cognome", placeholder: "Cognome", required: false, options: [] },
    { id: "email", key: "email", type: "email", label: "Email", placeholder: "Email", required: true, options: [] },
    { id: "phone", key: "phone", type: "phone", label: "Telefono", placeholder: "Telefono", required: false, options: [] },
    { id: "company", key: "company", type: "text", label: "Azienda", placeholder: "Azienda", required: false, options: [] },
    { id: "message", key: "message", type: "textarea", label: "Messaggio", placeholder: "Messaggio", required: false, options: [] },
  ];
}

export const webFormInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  redirectUrl: z.string().trim().url().optional().nullable().or(z.literal("").transform(() => null)),
  fields: webFormFieldsSchema.optional(),
  submitLabel: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .optional()
    .nullable()
    .or(z.literal("").transform(() => null)),
});
export type WebFormInput = z.infer<typeof webFormInputSchema>;

export const webFormSubmissionSchema = z.object({
  firstName: z.string().trim().min(1).max(120),
  lastName: z.string().trim().max(120).default(""),
  email: z.string().trim().toLowerCase().email(),
  phone: z.string().trim().max(40).optional().nullable(),
  company: z.string().trim().max(200).optional().nullable(),
  message: z.string().trim().max(5000).optional().nullable(),
});

// ── invio singolo ───────────────────────────────────────────────────
export const sendContactEmailSchema = z.object({
  templateId: z.string().uuid().optional(),
  subject: z.string().trim().max(300).optional(),
  bodyHtml: z.string().trim().max(100000).optional(),
});
